/* JD Gourmét — POUR: the scroll-scrubbed motion engine (verb #4, see jd-theme.css §10).
   One passive scroll listener + one rAF. Writes --jd-scrub (0..1) as an inline
   custom property on [data-jd-scrub] elements; CSS does all the choreography.
   Decorative only — never gates content visibility. No dependencies.

   Deliberately stateless per frame (v2): every rAF re-reads each tracked
   element's rect and skips writes for off-screen elements and unchanged
   values. With a handful of elements per page this costs less than the
   IntersectionObserver bookkeeping it replaces (v1's IO gating died silently
   after CDN minification — fewer moving parts wins).

   Modes (data-jd-scrub value):
     "view"  progress of the element THROUGH the viewport (0 = top edge enters
             at the bottom, 1 = bottom edge leaves at the top). Default.
     "exit"  progress of the element scrolling OUT (0 while its top is at/below
             the viewport top, 1 once fully scrolled past). Heroes.
     "track" progress of a tall scroll-track whose sticky child pins for
             (height − viewport) px. Scrollytelling scenes.
     "page"  whole-document progress, written on the element itself (NEVER on
             <html> — a root write invalidates style for the entire document
             every frame). Desktop only (its sole consumer is hidden < 990px).
             Optional data-jd-scrub-from="<selector>": progress stays 0 until
             that marker's bottom scrolls past the viewport top (e.g. the pour
             line starts BELOW the hero).

   Counters: [data-jd-count] animates the leading integer of its text up from 0
   on first intersection, preserving prefix/suffix ("20+" stays "20+").

   Reduced motion: the engine never starts and html.jd-scrub-on is never added —
   all consuming CSS is gated on that class, so everything rests in its final,
   static state. Counters render their authored values untouched. */
(function () {
  'use strict';

  var reduce =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mqPage =
    window.matchMedia && window.matchMedia('(min-width: 990px)');
  window.JD = window.JD || {};

  /* ---- Counters ------------------------------------------------------------ */
  function initCounters() {
    var els = document.querySelectorAll('[data-jd-count]:not([data-jd-counted])');
    els.forEach(function (el) {
      el.setAttribute('data-jd-counted', '1');
      var raw = el.textContent;
      var m = raw.match(/\d[\d,]*/);
      if (!m) return;
      var target = parseInt(m[0].replace(/,/g, ''), 10);
      if (!isFinite(target) || target <= 0) return;
      if (reduce || !('IntersectionObserver' in window)) return; /* keep authored text */
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(el);
          var start = null;
          var dur = Math.min(1400, 600 + target * 12);
          function frame(ts) {
            if (start === null) start = ts;
            var t = Math.min(1, (ts - start) / dur);
            var eased = 1 - Math.pow(1 - t, 3); /* ease-out cubic */
            el.textContent = raw.replace(m[0], Math.round(target * eased).toLocaleString());
            if (t < 1) requestAnimationFrame(frame);
            else el.textContent = raw; /* final text EXACTLY equals authored */
          }
          requestAnimationFrame(frame);
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ---- Scrub engine ---------------------------------------------------------- */
  var tracked = [];
  var ticking = false;
  /* Pour-line occlusion: the drizzle hides behind the flavor-marquee + its curved
     seam. Cached per scan; re-resolved after section reloads. seamRise = how far
     the next section's curve reaches below the marquee box (so the line emerges
     onto the section BELOW the curve, not mid-curve). */
  var pourEl = null, marqEl = null, seamRise = 56, pmLast = '';

  function measureSeamRise() {
    var d = document.createElement('div');
    d.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;height:var(--jd-seam-rise);';
    document.body.appendChild(d);
    var h = d.offsetHeight;
    d.remove();
    return h > 0 ? h : 56;
  }

  function scan() {
    tracked = [];
    pourEl = document.querySelector('.jd-pour-line');
    marqEl = document.querySelector('.jd-marquee');
    seamRise = measureSeamRise();
    pmLast = '';
    var els = document.querySelectorAll('[data-jd-scrub]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var fromSel = el.getAttribute('data-jd-scrub-from');
      var fromEl = null;
      if (fromSel) {
        try { fromEl = document.querySelector(fromSel); } catch (e) { fromEl = null; }
      }
      tracked.push({
        el: el,
        mode: el.getAttribute('data-jd-scrub') || 'view',
        fromEl: fromEl,
        last: el.style.getPropertyValue('--jd-scrub')
      });
    }
  }

  function update() {
    ticking = false;
    var vh = window.innerHeight;
    var y = window.scrollY || 0;
    var docH = document.documentElement.scrollHeight;
    /* All reads first, then all writes — never interleave (layout thrash). */
    var writes = [];
    for (var i = 0; i < tracked.length; i++) {
      var item = tracked[i];
      if (!item.el.isConnected) continue;
      var p;
      if (item.mode === 'page') {
        if (mqPage && !mqPage.matches) continue; /* consumer hidden on mobile */
        var startY = 0;
        if (item.fromEl && item.fromEl.isConnected) {
          startY = item.fromEl.getBoundingClientRect().bottom + y;
        }
        p = (y - startY) / Math.max(1, docH - vh - startY);
      } else {
        var r = item.el.getBoundingClientRect();
        /* Off-screen (with a generous margin): nothing the eye could see. */
        if (r.bottom < -vh * 0.3 || r.top > vh * 1.3) continue;
        if (item.mode === 'exit') {
          p = -r.top / Math.max(1, r.height);
        } else if (item.mode === 'track') {
          p = -r.top / Math.max(1, r.height - vh);
        } else {
          p = (vh - r.top) / Math.max(1, vh + r.height);
        }
      }
      if (p < 0) p = 0; else if (p > 1) p = 1;
      var v = p.toFixed(3);
      if (v !== item.last) {
        item.last = v;
        writes.push([item.el, v]);
      }
    }
    for (var j = 0; j < writes.length; j++) {
      writes[j][0].style.setProperty('--jd-scrub', writes[j][1]);
    }

    /* Pour-line mask band: where the marquee (and the curve below it) currently
       sits in the viewport, so the drizzle stays hidden until it clears the curve.
       Desktop only (the line is hidden < 990px). Off-screen marquee → 150vh = no-op. */
    if (pourEl && pourEl.isConnected && (!mqPage || mqPage.matches)) {
      var pmTop = '150vh', pmBot = '150vh';
      if (marqEl && marqEl.isConnected) {
        var mr = marqEl.getBoundingClientRect();
        if (mr.bottom > 0 && mr.top < vh) {
          pmTop = mr.top.toFixed(1) + 'px';
          pmBot = (mr.bottom + seamRise).toFixed(1) + 'px';
        }
      }
      var pm = pmTop + '|' + pmBot;
      if (pm !== pmLast) {
        pmLast = pm;
        /* Write the FULL mask gradient with LITERAL px (not via custom props):
           a var()-driven length inside a mask-image gradient silently no-ops in
           Safari and is fragile under Shopify CDN minification — when it dropped,
           the mask went all-opaque and the rail + its glass bead bled straight
           through the marquee band. Literal px survive both. 9px soft edges. */
        var grad = 'linear-gradient(to bottom,#000 0,#000 calc(' + pmTop +
          ' - 9px),transparent ' + pmTop + ',transparent ' + pmBot +
          ',#000 calc(' + pmBot + ' + 9px),#000 100%)';
        pourEl.style.webkitMaskImage = grad;
        pourEl.style.maskImage = grad;
        /* keep the props in sync for any inspector / static fallback read */
        pourEl.style.setProperty('--jd-pm-top', pmTop);
        pourEl.style.setProperty('--jd-pm-bottom', pmBot);
      }
    }
  }

  function schedule() {
    if (ticking || !tracked.length) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  window.JD.scrub = {
    refresh: function () {
      scan();
      initCounters();
      /* Synchronous pass, not schedule(): rAF never fires in occluded tabs
         (theme-editor iframes, automation), and a refresh must land NOW. */
      update();
    }
  };

  function boot() {
    initCounters();
    if (reduce) return; /* counters only; no scrub layer */
    document.documentElement.classList.add('jd-scrub-on');
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    scan();
    schedule();
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  window.addEventListener('load', schedule);

  /* Theme editor lifecycle — same idiom as jd-theme.js. */
  document.addEventListener('shopify:section:load', function () { window.JD.scrub.refresh(); });
  document.addEventListener('shopify:section:unload', function () { window.JD.scrub.refresh(); });
})();
